import React from 'react';
import { Box, Checkbox, FormControlLabel } from '@mui/material';

import { MaterialPropertyValuePair, PropertySelectField, PropertyTextInput } from '@paragon/client-tools';
import { Distribution, LoadType, MemberType, MovingLoadSettings } from '@paragon/contract-library';
import { DistanceField, NumericField } from '@paragon/material';
import { humanCase } from '@paragon/utilities';

import { getDistanceFormatterSelector } from '../../../../store/selectors/getDistanceFormatterSelector';
import { useAppSelector } from '../../../hooks';
import { PsfPlfToggle } from '../elements/PsfPlfToggle';
import { magnitudeUnitToEndAdornmentText } from '../manual-loads/LoadMagnitudeSettings';

const loadDistributionOptions: Distribution[] = ['Concentrated', 'Distributed'];
const loadTypeOptions: LoadType[] = ['Dead', 'LiveRoof', 'LiveFloor'];
const memberTypeOptions: readonly MemberType[] = ['TopChord', 'BottomChord'];

interface Props {
  movingLoadSettings: MovingLoadSettings;
  trussSpacing: number;
  onChange: (movingLoadSettings: MovingLoadSettings) => void;
  disabled?: boolean;
}

export function MovingLoadProperties(props: Props) {
  const { disabled, movingLoadSettings, trussSpacing } = props;
  const distanceFormatter = useAppSelector(getDistanceFormatterSelector);

  const onChange = (partialMovingLoadSettings: Partial<MovingLoadSettings>) =>
    props.onChange({ ...movingLoadSettings, ...partialMovingLoadSettings });

  return (
    <>
      <PropertySelectField
        property="Load Distribution"
        value={movingLoadSettings.loadDistribution}
        options={loadDistributionOptions}
        getName={humanCase}
        submit={(loadDistribution) =>
          onChange({
            loadDistribution,
            magnitudeUnit: loadDistribution === 'Concentrated' ? 'Pounds' : 'PoundsPerSquareFoot',
          })
        }
        disabled={disabled}
      />

      <Box sx={{ display: 'flex' }}>
        <NumericField
          label="Load Magnitude"
          value={movingLoadSettings.loadMagnitude}
          onSubmit={(loadMagnitude) => onChange({ loadMagnitude })}
          endAdornment={magnitudeUnitToEndAdornmentText(movingLoadSettings.magnitudeUnit)}
          disabled={disabled}
        />
        {movingLoadSettings.loadDistribution === 'Distributed' && movingLoadSettings.magnitudeUnit !== 'Pounds' && (
          <PsfPlfToggle
            magnitudeUnit={movingLoadSettings.magnitudeUnit}
            onChange={(magnitudeUnit) => onChange({ magnitudeUnit })}
            disabled={disabled}
          />
        )}
      </Box>

      {movingLoadSettings.loadDistribution === 'Distributed' && movingLoadSettings.magnitudeUnit !== 'Pounds' && (
        <MaterialPropertyValuePair property="Truss Spacing" value={distanceFormatter(trussSpacing)} />
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={movingLoadSettings.usePanelPoints}
            disabled={disabled}
            onChange={(event, checked) => onChange({ usePanelPoints: checked })}
          />
        }
        label="Use Panel and Mid-Panel Points?"
      />

      {movingLoadSettings.loadDistribution === 'Distributed' && (
        <DistanceField
          label="Distributed Load Length"
          value={movingLoadSettings.distributedLoadLength}
          onSubmit={(distributedLoadLength) => onChange({ distributedLoadLength })}
          disabled={disabled}
        />
      )}

      <DistanceField
        label="User Defined Start Location"
        value={movingLoadSettings.startLocationX}
        onSubmit={(startLocationX) => onChange({ startLocationX })}
        disabled={disabled || movingLoadSettings.usePanelPoints}
      />

      <DistanceField
        label="User Defined End Location"
        value={movingLoadSettings.endLocationX}
        onSubmit={(endLocationX) => onChange({ endLocationX })}
        disabled={disabled || movingLoadSettings.usePanelPoints}
      />

      <DistanceField
        label="User Defined Distance Between Loads"
        value={movingLoadSettings.distanceBetweenLoads}
        onSubmit={(distanceBetweenLoads) => onChange({ distanceBetweenLoads })}
        disabled={disabled || movingLoadSettings.usePanelPoints}
      />

      <PropertySelectField
        property="Load Type"
        value={movingLoadSettings.loadType}
        options={loadTypeOptions}
        getName={humanCase}
        submit={(loadType) => onChange({ loadType })}
        disabled={disabled}
      />

      <PropertySelectField
        property="Along Member Type"
        value={movingLoadSettings.alongMemberType}
        options={memberTypeOptions}
        getName={humanCase}
        submit={(alongMemberType) => onChange({ alongMemberType })}
        disabled={disabled}
      />

      <PropertyTextInput
        fullWidth
        property="Moving Load Name (Optional)"
        value={movingLoadSettings.name ?? ''}
        submit={(name) =>
          onChange({
            name: name === '' ? null : name,
          })
        }
        disabled={disabled}
      />
    </>
  );
}
